import { Badge, Banner, Button, Card, Modal } from '@ths/ui';
import { Navigate } from 'hotel/navigation';
import { useRef, useState } from 'react';

import {
  HotelSavedCardStatus,
  UserRole,
  useMyHotelSavedCardsQuery,
  useRemoveHotelSavedCardMutation,
  useSetDefaultHotelSavedCardMutation,
} from 'generated/graphql';
import { useAppSelector } from 'store/store';

import { HotelCardSetupModal } from '../../hotel/HotelCardSetupModal';
import { SavedCardsSkeleton } from '../../hotel/SavedCardsSkeleton';
import { notifyHotelSavedCardsChanged } from '../../hotel/savedCardReturn';
import { useHotelScope } from '../../hotel/scope';
import { useHotelCardSetup } from '../../hotel/useHotelCardSetup';
import { useHotelSavedCardRefresh } from '../../hotel/useHotelSavedCardRefresh';

export default function PaymentMethods() {
  const enabled = useAppSelector(
    (state) => state.enums.featureFlags?.flags.hotelSavedCards === true,
  );
  const owner = useAppSelector((state) => state.user.id);
  const scope = useHotelScope();
  if (!enabled) return <Navigate to="/dashboard" replace />;
  const identity = `${owner}:${scope?.clientId}:${scope?.stateKey ?? ''}`;
  return <PaymentMethodsContent key={identity} identity={identity} />;
}

function PaymentMethodsContent({ identity }: { identity: string }) {
  const roles = useAppSelector((state) => state.user.roles ?? []);
  const canManage =
    roles.includes(UserRole.ClientAdmin) ||
    roles.includes(UserRole.ClientUser) ||
    (roles.includes(UserRole.ClientTraveler) &&
      !roles.includes(UserRole.ClientReadOnly));
  const cards = useMyHotelSavedCardsQuery({
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  useHotelSavedCardRefresh(true, cards.refetch);
  const setup = useHotelCardSetup(identity, cards.refetch);
  const [remove, { loading: removing }] = useRemoveHotelSavedCardMutation();
  const [setDefault, { loading: settingDefault }] =
    useSetDefaultHotelSavedCardMutation({ fetchPolicy: 'no-cache' });
  const [defaultPending, setDefaultPending] = useState<string | null>(null);
  const [defaultError, setDefaultError] = useState<string | null>(null);
  type Method = NonNullable<
    typeof cards.data
  >['myHotelSavedCards']['methods'][number];
  const [confirm, setConfirm] = useState<Method | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [notice, setNotice] = useState('');
  const returnFocus = useRef<HTMLButtonElement | null>(null);
  const methods = (cards.data?.myHotelSavedCards.methods ?? []).filter(
    (method) => method.status !== HotelSavedCardStatus.Removed,
  );
  const configured = cards.data?.myHotelSavedCards.configured === true;
  const busy =
    cards.loading ||
    !!setup.state ||
    removing ||
    settingDefault ||
    !!defaultPending;
  const name = (method: Method) => `${method.brand} ending in ${method.last4}`;

  function closeConfirmation() {
    setConfirm(null);
    returnFocus.current?.focus();
  }
  async function removeCard() {
    if (!confirm || removing) return;
    setRemoveError(null);
    try {
      await remove({ variables: { methodId: confirm.id } });
    } catch {
      setRemoveError('Unable to remove this card. Please try again.');
      return;
    }
    closeConfirmation();
    setNotice('Card removed.');
    notifyHotelSavedCardsChanged();
    void cards.refetch().catch(() => {});
  }

  async function makeDefault(method: Method) {
    if (busy) return;
    setDefaultPending(method.id);
    setDefaultError(null);
    setNotice('');
    try {
      await setDefault({ variables: { methodId: method.id } });
      notifyHotelSavedCardsChanged();
      await cards.refetch();
      setNotice(`${name(method)} is now your default payment method.`);
    } catch {
      setDefaultError(
        'Unable to update the default payment method. Please try again.',
      );
    } finally {
      setDefaultPending(null);
    }
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6 px-4 py-8 text-text md:px-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <h1 className="text-3xl font-bold text-text">Payment methods</h1>
        {canManage && (
          <Button
            ref={setup.returnFocus}
            type="button"
            onClick={() => void setup.addCard()}
            loading={setup.loading}
            disabled={busy || !configured || !!cards.error}
          >
            Add a card
          </Button>
        )}
      </div>
      <p role="status" className="text-body text-text-secondary">
        {notice}
      </p>
      <HotelCardSetupModal setup={setup} />
      {defaultError && <Banner status="error">{defaultError}</Banner>}
      {cards.loading && !cards.data ? (
        <SavedCardsSkeleton />
      ) : cards.error ? (
        <Banner status="error">
          Unable to check saved cards. Reload this page and try again.
        </Banner>
      ) : !configured ? (
        <Banner status="warning">
          Card management is currently unavailable. Contact THS for assistance.
        </Banner>
      ) : (
        <Card>
          {methods.length ? (
            <ul className="divide-y divide-separator" aria-label="Saved cards">
              {methods.map((method) => (
                <li
                  key={method.id}
                  className="flex flex-wrap items-center justify-between gap-4 p-5"
                >
                  <div className="min-w-0 space-y-1">
                    <h2 className="text-body font-medium">
                      <span className="capitalize">{method.brand}</span> ending
                      in {method.last4}
                    </h2>
                    {method.isDefault && (
                      <Badge status="neutral">Default</Badge>
                    )}
                    <p className="text-body text-text-secondary">
                      Expires {String(method.expiryMonth).padStart(2, '0')}/
                      {method.expiryYear}
                    </p>
                    {method.status === HotelSavedCardStatus.Expired && (
                      <p className="text-label text-warning-text">Expired</p>
                    )}
                    {method.status === HotelSavedCardStatus.Unknown && (
                      <p className="text-label text-warning-text">
                        Status unavailable
                      </p>
                    )}
                  </div>
                  {canManage && (
                    <div className="flex flex-wrap gap-2">
                      {!method.isDefault && (
                        <Button
                          type="button"
                          variant="neutral"
                          size="sm"
                          aria-label={`Set ${name(method)} as default`}
                          disabled={
                            busy ||
                            method.status !== HotelSavedCardStatus.Available
                          }
                          loading={defaultPending === method.id}
                          onClick={() => void makeDefault(method)}
                        >
                          Set as default
                        </Button>
                      )}
                      <Button
                        type="button"
                        variant="neutral"
                        size="sm"
                        aria-label={`Remove ${name(method)}`}
                        disabled={busy}
                        onClick={(event) => {
                          returnFocus.current = event.currentTarget;
                          setRemoveError(null);
                          setNotice('');
                          setConfirm(method);
                        }}
                      >
                        Remove
                      </Button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="p-5 text-body text-text-secondary">
              You have no saved cards.
            </p>
          )}
        </Card>
      )}
      <Modal
        open={!!confirm}
        onOpenChange={(open) => {
          if (!open && !removing) closeConfirmation();
        }}
        title="Remove card?"
        description={
          confirm ? `Remove ${name(confirm)} from your saved cards?` : undefined
        }
        footer={
          <>
            <Button
              type="button"
              variant="neutral"
              disabled={removing}
              onClick={closeConfirmation}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="danger"
              loading={removing}
              onClick={() => void removeCard()}
            >
              Remove card
            </Button>
          </>
        }
      >
        <p className="text-body text-text-secondary">
          You can add it again for future bookings. Past bookings keep their
          recorded card details.
        </p>
        {removeError && <Banner status="error">{removeError}</Banner>}
      </Modal>
    </div>
  );
}
